import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SettingsIcon from '@mui/icons-material/Settings';
import { motion } from 'framer-motion';

const treatmentSystems = [
  {
    id: 'etp',
    title: 'Effluent Treatment Plant',
    subtitle: 'Industrial Waste Processing',
    description: 'Advanced effluent treatment solutions designed for industrial waste processing with high efficiency and environmental compliance.',
    image: '/images/products/etp.jpg',
    features: [
      'Custom designed based on effluent characteristics',
      'Advanced treatment technologies',
      'Automated control systems',
      'Environmental compliance monitoring',
      'Sludge management systems'
    ],
    tags: ['Industrial', 'High Efficiency', 'Automated']
  },
  {
    id: 'cetp',
    title: 'Common Effluent Treatment',
    subtitle: 'Centralized Processing',
    description: 'Centralized treatment facilities serving multiple industrial units with shared waste processing capabilities.',
    image: '/images/products/cetp.jpg',
    features: [
      'Centralized processing facility',
      'Multi-stage treatment process',
      'Shared infrastructure benefits',
      'Cost-effective solution',
      'Standardized treatment protocols'
    ],
    tags: ['Centralized', 'Multi-stage', 'Cost-effective']
  },
  {
    id: 'sewage',
    title: 'Sewage Treatment',
    subtitle: 'Municipal Solutions',
    description: 'Comprehensive sewage treatment solutions for municipal and industrial applications.',
    image: '/images/products/sewage.jpg',
    features: [
      'Biological treatment processes',
      'Nutrient removal systems',
      'Odor control mechanisms',
      'Sludge handling facilities',
      'Treated water reuse options'
    ],
    tags: ['Municipal', 'Biological', 'Water Reuse']
  },
  {
    id: 'oil-water',
    title: 'Oil Water Separator',
    subtitle: 'Specialized Separation',
    description: 'Specialized systems for separating oil and grease from industrial wastewater.',
    image: '/images/products/oil-water.jpg',
    features: [
      'High separation efficiency',
      'Continuous operation capability',
      'Low maintenance design',
      'Automated skimming system',
      'Multiple stage separation'
    ],
    tags: ['Oil Separation', 'Automated', 'Low Maintenance']
  },
  {
    id: 'recycling',
    title: 'Industrial Recycling',
    subtitle: 'Sustainable Solutions',
    description: 'Water recycling solutions for industrial applications, promoting sustainability and resource conservation.',
    image: '/images/products/recycling.jpg',
    features: [
      'Advanced filtration systems',
      'Quality monitoring systems',
      'Resource recovery options',
      'Customized recycling solutions',
      'Water quality management'
    ],
    tags: ['Recycling', 'Sustainable', 'Resource Recovery']
  },
  {
    id: 'stp',
    title: 'Packaged STP',
    subtitle: 'Compact Solutions',
    description: 'Compact and pre-engineered sewage treatment plants for smaller installations.',
    image: '/images/products/stp.jpg',
    features: [
      'Space-efficient design',
      'Quick installation',
      'Low maintenance requirements',
      'Modular construction',
      'Plug and play operation'
    ],
    tags: ['Compact', 'Pre-engineered', 'Modular']
  },
  {
    id: 'zld',
    title: 'Zero Liquid Discharge (ZLD)',
    subtitle: 'Complete Recovery',
    description: 'Complete water recovery systems achieving zero liquid discharge for maximum environmental compliance.',
    image: '/images/products/zld.jpg',
    features: [
      'Complete water recovery',
      'Minimal environmental impact',
      'Advanced evaporation systems',
      'Crystallization technology',
      'Waste minimization'
    ],
    tags: ['Zero Discharge', 'Environmental', 'Advanced']
  },
  {
    id: 'dm',
    title: 'DM Plant',
    subtitle: 'High Purity Solutions',
    description: 'Demineralization plants for producing high-purity water for industrial applications.',
    image: '/images/products/dm.jpg',
    features: [
      'Ion exchange technology',
      'Automated regeneration',
      'High purity water output',
      'Process monitoring systems',
      'Custom capacity options'
    ],
    tags: ['Demineralization', 'High Purity', 'Automated']
  }
];

const WasteWaterTreatment = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <Box sx={{ bgcolor: '#f8f9fa' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '300px', md: '400px' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to right, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.45))',
            zIndex: 1
          }
        }}
      >
        <Box
          component="img"
          src="/images/bio.jpg"
          alt="Water Treatment"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
        <Container 
          maxWidth="lg" 
          sx={{ 
            position: 'relative', 
            zIndex: 2,
            pl: { xs: 2, md: 4 },
            pr: { xs: 2, md: 4 }
          }}
        >
          <Box
            sx={{
              maxWidth: '800px',
              position: 'relative'
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: 'white',
                fontWeight: 600,
                fontSize: { xs: '2rem', md: '3rem' },
                lineHeight: 1.2,
                mb: 1,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-12px',
                  left: '0',
                  width: '60px',
                  height: '4px',
                  backgroundColor: '#0288d1',
                  borderRadius: '2px'
                }
              }}
            >
              Waste Water Treatment Systems
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#e0e0e0',
                maxWidth: '600px',
                fontSize: { xs: '1rem', md: '1.25rem' },
                lineHeight: 1.5,
                fontWeight: 400,
                mt: 3
              }}
            >
              Advanced solutions for sustainable water management and environmental compliance
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Systems Grid */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={4}>
          {treatmentSystems.map((system, index) => (
            <Grid item xs={12} md={6} key={system.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  onMouseEnter={() => setHoveredCard(system.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: hoveredCard === system.id 
                      ? '0 16px 40px rgba(0,0,0,0.12)' 
                      : '0 4px 12px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)'
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="240"
                    image={system.image}
                    alt={system.title}
                    sx={{
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '30%',
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))'
                      }
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                      <WaterDropIcon sx={{ color: '#0288d1', fontSize: 28 }} />
                      <Box>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#0288d1' }}>
                          {system.title}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                          {system.subtitle}
                        </Typography>
                      </Box>
                    </Stack>

                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
                      {system.description}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3, gap: 1 }}>
                      {system.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(2,136,209,0.1)',
                            color: '#0288d1',
                            fontWeight: 500
                          }}
                        />
                      ))}
                    </Stack>

                    <Divider sx={{ mb: 3 }} />

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" color="text.secondary">
                        View Details
                      </Typography>
                      <IconButton
                        sx={{
                          color: '#0288d1',
                          '&:hover': {
                            bgcolor: 'rgba(2,136,209,0.1)'
                          }
                        }}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default WasteWaterTreatment;

 